import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Loader2, Pencil, Trash2, ArrowUp, ArrowDown, CheckCircle2, Send,
  CalendarClock, Undo2, Archive, Eye, X, Search,
} from "lucide-react";
import { Block } from "@/pages/Article";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const STATUS_STYLES = {
  draft: "text-[#A1A1AA] border-[#27272A]",
  approved: "text-[#7DD3FC] border-[#7DD3FC]/40",
  scheduled: "text-[#FBBF24] border-[#FBBF24]/40",
  published: "text-[#DFFF00] border-[#DFFF00]/40",
  archived: "text-[#52525B] border-[#27272A]",
};

const fmt = (d) => (d ? new Date(d).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—");

const EMPTY = {
  slug: "", title: "", excerpt: "", intro: "", category: "", date: "",
  readingTime: "", seoTitle: "", seoDescription: "", body: [{ type: "p", content: "" }], cta: null,
};

function StatusBadge({ status }) {
  return (
    <span data-testid="article-status-badge" className={`inline-flex text-[11px] font-body font-bold uppercase tracking-wider border px-2.5 py-1 ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      {status}
    </span>
  );
}

function BlockEditor({ body, onChange }) {
  const update = (i, patch) => onChange(body.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= body.length) return;
    const next = [...body];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {body.map((b, i) => (
        <div key={i} className="border border-[#27272A] p-3" data-testid={`block-editor-${i}`}>
          {!["p", "h2", "ul"].includes(b.type) ? (
            <p className="font-body text-xs text-[#A1A1AA]">
              "{b.type}" block ({Array.isArray(b.content) ? b.content.length : 0} items) — edited by the site team, left unchanged on save.
            </p>
          ) : (
          <>
          <div className="flex items-center gap-2 mb-2">
            <select
              data-testid={`block-type-${i}`}
              value={b.type}
              onChange={(e) => {
                const type = e.target.value;
                const content = type === "ul"
                  ? (Array.isArray(b.content) ? b.content : String(b.content || "").split("\n").filter(Boolean))
                  : (Array.isArray(b.content) ? b.content.join("\n") : b.content);
                update(i, { type, content });
              }}
              className="bg-[#0A0A0A] border border-[#27272A] font-body text-xs text-[#A1A1AA] px-2 py-1.5"
            >
              <option value="p">Paragraph</option>
              <option value="h2">Heading</option>
              <option value="ul">Bullet list</option>
            </select>
            <div className="ml-auto flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1.5 text-[#52525B] hover:text-[#DFFF00]" aria-label="Move up"><ArrowUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1.5 text-[#52525B] hover:text-[#DFFF00]" aria-label="Move down"><ArrowDown size={14} /></button>
              <button type="button" data-testid={`block-delete-${i}`} onClick={() => onChange(body.filter((_, j) => j !== i))} className="p-1.5 text-[#52525B] hover:text-red-400" aria-label="Delete block"><Trash2 size={14} /></button>
            </div>
          </div>
          <textarea
            data-testid={`block-content-${i}`}
            value={Array.isArray(b.content) ? b.content.join("\n") : b.content}
            onChange={(e) => update(i, { content: b.type === "ul" ? e.target.value.split("\n") : e.target.value })}
            placeholder={b.type === "ul" ? "One bullet point per line" : b.type === "h2" ? "Heading text" : "Paragraph text"}
            rows={b.type === "h2" ? 1 : 3}
            className="w-full bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] p-3 focus:border-[#DFFF00] outline-none"
          />
          </>
          )}
        </div>
      ))}
      <button
        type="button" data-testid="block-add-btn"
        onClick={() => onChange([...body, { type: "p", content: "" }])}
        className="flex items-center gap-2 font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00] transition-colors"
      >
        <Plus size={15} /> Add block
      </button>
    </div>
  );
}

function Editor({ article, onClose, onSaved }) {
  const isNew = !article.id;
  const [form, setForm] = useState({ ...EMPTY, ...article, cta: article.cta || null });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const slugLocked = !!article.published_once;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        slug: form.slug || null, title: form.title, excerpt: form.excerpt, intro: form.intro,
        category: form.category, date: form.date || null, readingTime: form.readingTime,
        seoTitle: form.seoTitle, seoDescription: form.seoDescription,
        body: form.body.map((b) => (b.type === "ul" ? { ...b, content: (Array.isArray(b.content) ? b.content : []).filter((l) => l.trim()) } : b)),
        cta: form.cta && form.cta.text ? form.cta : null,
      };
      const r = isNew
        ? await http.post("/admin/articles", payload)
        : await http.put(`/admin/articles/${article.id}`, { ...payload, expected_revision: article.revision });
      toast.success(isNew ? "Draft created." : "Article saved.");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the article.");
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div>
      <label className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">{label}</label>
      <input
        data-testid={`article-field-${key}`}
        value={form[key] || ""}
        onChange={(e) => set(key, e.target.value)}
        {...props}
        className={`w-full bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] px-3 py-2.5 focus:border-[#DFFF00] outline-none ${props.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      />
    </div>
  );

  return (
    <div data-testid="article-editor" className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4 md:p-10">
      <div className="w-full max-w-3xl bg-[#141414] border border-[#27272A]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#27272A] sticky top-0 bg-[#141414] z-10">
          <p className="font-display font-bold text-[#F8F8F5]">{isNew ? "New article draft" : `Edit: ${article.title}`}</p>
          <div className="flex items-center gap-3">
            <button data-testid="article-preview-toggle" onClick={() => setPreview((p) => !p)} className="flex items-center gap-1.5 font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00]">
              <Eye size={15} /> {preview ? "Edit" : "Preview"}
            </button>
            <button data-testid="article-editor-close" onClick={onClose} className="p-1.5 text-[#52525B] hover:text-[#F8F8F5]" aria-label="Close editor"><X size={18} /></button>
          </div>
        </div>

        {preview ? (
          <div className="px-6 py-8 bg-[#0A0A0A]">
            <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#DFFF00]">{form.category}</p>
            <h1 className="mt-4 font-display font-bold tracking-tighter leading-[1.05] text-3xl md:text-4xl text-[#F8F8F5]">{form.title}</h1>
            {form.intro && <p className="mt-6 font-body text-lg text-[#F8F8F5] leading-relaxed border-l-2 border-[#DFFF00] pl-5">{form.intro}</p>}
            <div className="mt-6">
              {form.body.map((b, i) => <Block key={i} block={b} />)}
            </div>
          </div>
        ) : (
          <div className="px-6 py-6 space-y-4">
            {field("Title", "title")}
            <div className="grid sm:grid-cols-2 gap-4">
              {field(slugLocked ? "Slug (locked after publish)" : "Slug (blank = auto)", "slug", { disabled: slugLocked })}
              {field("Category", "category")}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {field("Display date (YYYY-MM-DD)", "date", { placeholder: "Blank = date of publish" })}
              {field("Reading time", "readingTime", { placeholder: "e.g. 4 min read" })}
            </div>
            <div>
              <label className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">Excerpt (listing page)</label>
              <textarea data-testid="article-field-excerpt" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2}
                className="w-full bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] p-3 focus:border-[#DFFF00] outline-none" />
            </div>
            <div>
              <label className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">Intro (highlighted lead paragraph, optional)</label>
              <textarea data-testid="article-field-intro" value={form.intro} onChange={(e) => set("intro", e.target.value)} rows={2}
                className="w-full bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] p-3 focus:border-[#DFFF00] outline-none" />
            </div>
            {field("SEO title (blank = article title)", "seoTitle", { placeholder: "e.g. Age Discrimination at Work | Barrett Career Brand" })}
            <div>
              <label className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B]">SEO description</label>
              <textarea data-testid="article-field-seo" value={form.seoDescription} onChange={(e) => set("seoDescription", e.target.value)} rows={2}
                className="w-full bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] p-3 focus:border-[#DFFF00] outline-none" />
            </div>
            <div>
              <p className="tracking-label text-[10px] uppercase font-body font-bold text-[#52525B] mb-2">Body</p>
              <BlockEditor body={form.body} onChange={(b) => set("body", b)} />
            </div>
            <div className="border border-[#27272A] p-4">
              <label className="flex items-center gap-2 font-body text-sm text-[#A1A1AA] cursor-pointer">
                <input type="checkbox" data-testid="article-cta-toggle" className="accent-[#DFFF00]" checked={!!form.cta}
                  onChange={(e) => set("cta", e.target.checked ? { text: "", label: "", href: "/#consultation" } : null)} />
                Custom call-to-action (otherwise the default "free score" CTA shows)
              </label>
              {form.cta && (
                <div className="mt-3 grid sm:grid-cols-3 gap-3">
                  {["text", "label", "href"].map((k) => (
                    <input key={k} data-testid={`article-cta-${k}`} placeholder={k} value={form.cta[k] || ""}
                      onChange={(e) => set("cta", { ...form.cta, [k]: e.target.value })}
                      className="bg-[#0A0A0A] border border-[#27272A] font-body text-sm text-[#F8F8F5] px-3 py-2 focus:border-[#DFFF00] outline-none" />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-[#27272A] flex justify-end gap-3">
          <button onClick={onClose} className="font-body text-sm text-[#52525B] hover:text-[#F8F8F5] px-4 py-2">Cancel</button>
          <button data-testid="article-save-btn" onClick={save} disabled={saving}
            className="rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold text-sm px-6 py-2.5 flex items-center gap-2 hover:bg-[#C8E600] transition-colors disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Create draft" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleInput({ onSchedule, onCancel }) {
  const [dt, setDt] = useState("");
  return (
    <span className="inline-flex items-center gap-2">
      <input type="datetime-local" data-testid="article-schedule-input" value={dt} onChange={(e) => setDt(e.target.value)}
        className="bg-[#0A0A0A] border border-[#27272A] font-body text-xs text-[#A1A1AA] px-2 py-1.5 focus:border-[#DFFF00] outline-none" />
      <button data-testid="article-schedule-confirm" onClick={() => dt && onSchedule(new Date(dt).toISOString())}
        className="font-body text-xs font-semibold text-[#FBBF24] hover:underline">Confirm</button>
      <button onClick={onCancel} className="font-body text-xs text-[#52525B] hover:text-[#F8F8F5]">Cancel</button>
    </span>
  );
}

function ArticleRow({ a, onEdit, onTransition }) {
  const [scheduling, setScheduling] = useState(false);
  const act = (label, to, icon, testid, cls = "text-[#A1A1AA] hover:text-[#DFFF00]") => (
    <button key={to + label} data-testid={testid} onClick={() => onTransition(a, to)}
      className={`inline-flex items-center gap-1.5 font-body text-xs font-semibold transition-colors ${cls}`}>
      {icon} {label}
    </button>
  );
  return (
    <div data-testid={`article-row-${a.slug}`} className="border border-[#27272A] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={a.status} />
            <p className="font-display font-bold text-[#F8F8F5] break-words">{a.title}</p>
          </div>
          <p className="mt-2 font-body text-xs text-[#52525B]">
            /insights/{a.slug} · {a.category || "no category"} · updated {fmt(a.updated_at)} by {a.updated_by || "—"}
            {a.source === "n8n" && <span className="ml-2 text-[#7DD3FC]">via n8n</span>}
            {a.status === "scheduled" && <span className="ml-2 text-[#FBBF24]">publishes {fmt(a.publish_at)}</span>}
          </p>
        </div>
        <button data-testid={`article-edit-${a.slug}`} onClick={() => onEdit(a)}
          className="inline-flex items-center gap-1.5 font-body text-xs font-semibold text-[#A1A1AA] hover:text-[#DFFF00]">
          <Pencil size={13} /> Edit
        </button>
      </div>
      <div className="mt-3 pt-3 border-t border-[#27272A]/60 flex flex-wrap items-center gap-5">
        {a.status === "draft" && act("Approve", "approved", <CheckCircle2 size={13} />, `article-approve-${a.slug}`, "text-[#7DD3FC] hover:underline")}
        {a.status === "approved" && (
          <>
            {act("Publish now", "published", <Send size={13} />, `article-publish-${a.slug}`, "text-[#DFFF00] hover:underline")}
            {!scheduling && (
              <button data-testid={`article-schedule-${a.slug}`} onClick={() => setScheduling(true)}
                className="inline-flex items-center gap-1.5 font-body text-xs font-semibold text-[#FBBF24] hover:underline">
                <CalendarClock size={13} /> Schedule
              </button>
            )}
            {scheduling && <ScheduleInput onSchedule={(iso) => { setScheduling(false); onTransition(a, "scheduled", iso); }} onCancel={() => setScheduling(false)} />}
            {act("Back to draft", "draft", <Undo2 size={13} />, `article-todraft-${a.slug}`)}
          </>
        )}
        {a.status === "scheduled" && (
          <>
            {act("Publish now", "published", <Send size={13} />, `article-publish-${a.slug}`, "text-[#DFFF00] hover:underline")}
            {act("Cancel schedule", "draft", <Undo2 size={13} />, `article-unschedule-${a.slug}`)}
          </>
        )}
        {a.status === "published" && (
          <>
            <a href={`/insights/${a.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`article-view-${a.slug}`}
              className="inline-flex items-center gap-1.5 font-body text-xs font-semibold text-[#DFFF00] hover:underline">
              <Eye size={13} /> View live
            </a>
            {act("Unpublish", "draft", <Undo2 size={13} />, `article-unpublish-${a.slug}`)}
            {act("Archive", "archived", <Archive size={13} />, `article-archive-${a.slug}`)}
          </>
        )}
        {a.status === "archived" && act("Restore to draft", "draft", <Undo2 size={13} />, `article-restore-${a.slug}`)}
      </div>
    </div>
  );
}

export default function AdminArticles() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (status = filter, query = q) => {
    setLoading(true);
    try {
      const r = await http.get("/admin/articles", { params: { status, q: query } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Could not load articles.");
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => { load("", ""); /* eslint-disable-next-line */ }, []);

  const transition = async (a, to, publishAt = null) => {
    try {
      await http.post(`/admin/articles/${a.id}/transition`, { to, publish_at: publishAt });
      toast.success(to === "published" ? "Article published — it's live now." : `Moved to ${to}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change status.");
    }
  };

  return (
    <div data-testid="admin-articles" className="mt-8">
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="article-new-btn" onClick={() => setEditing({ ...EMPTY })}
          className="rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold text-sm px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#C8E600] transition-colors">
          <Plus size={15} /> New article
        </button>
        <div className="flex flex-wrap gap-1.5">
          {["", "draft", "approved", "scheduled", "published", "archived"].map((s) => (
            <button key={s || "all"} data-testid={`article-filter-${s || "all"}`}
              onClick={() => { setFilter(s); load(s, q); }}
              className={`rounded-full px-3.5 py-1.5 font-body text-xs transition-colors ${filter === s ? "bg-[#F8F8F5] text-[#0A0A0A] font-semibold" : "border border-[#27272A] text-[#A1A1AA] hover:text-[#F8F8F5]"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(filter, q); }} className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <input data-testid="article-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or slug"
            className="w-full bg-[#141414] border border-[#27272A] font-body text-sm text-[#F8F8F5] pl-9 pr-3 py-2.5 focus:border-[#DFFF00] outline-none" />
        </form>
      </div>

      <p data-testid="articles-count" className="font-body text-sm text-[#52525B] mt-6 mb-4">
        {loading ? "Loading…" : `${rows.length} article(s)`}
      </p>
      <div className="space-y-3" data-testid="article-rows">
        {rows.map((a) => (
          <ArticleRow key={a.id} a={a} onEdit={setEditing} onTransition={transition} />
        ))}
        {!loading && rows.length === 0 && <p className="font-body text-[#A1A1AA]">No articles found.</p>}
      </div>

      {editing && (
        <Editor
          article={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
